<template>
    <div class="bar-chart-template">
        <canvas :id="'chart-'+id" :class="(if_chart_has_data ? '':'hide')" class="bar-chart chart-template"></canvas>
        <div :class="(!if_chart_has_data && !if_refreshing ? 'd-inline-block':'hide')" class='text-center'>
            <h1 class="text-secondary mb-1"><i class="fas fa-chart-simple"></i></h1>
            <h4 class="text-secondary mb-2"><em>Press the <strong>Preview</strong> button to render the chart</em></h4>
        </div>
    </div>
</template>

<script>
import { defineComponent, markRaw } from 'vue';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ChartjsPluginStacked100 from "chartjs-plugin-stacked100";
import chartTooltip from '../../../../mixins/chart_tooltip';
import chartBase from '../../../../mixins/chart_base';

export default defineComponent({
    name: 'StackedHorizontalBarChartTemplate',
    mixins: [chartTooltip, chartBase],
    props: {
        chart_selected: {
            type: Object,
            required: true
        },
        id: {
            type: [String, Number],
            required: true
        },
        if_refreshing: {
            type: Boolean,
            default: false
        },
        if_on_report_page: {
            type: Boolean,
            default: false
        },
        if_benchmark_report: {
            type: Boolean,
            default: false
        },
        applied_chart_filter: {
            type: Object,
            default: () => ({})
        },
        if_hundred_percent: {
            type: Boolean,
            default: false
        },
        if_show_label: {
            type: Boolean,
            default: false
        },
        size: {
            type: String,
            default: 'small'
        },
        section_id: {
            type: [String, Number],
            default: null
        },
        show_compressed_numbers: {
            type: Boolean,
            default: true
        },
        forceShowAllData: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            format_type: 'number',
            color: 'yellow',
            total_values: [],
            /** When true, next chart render (e.g. after resize) uses no animation. Cleared after render. */
            skipAnimationForNextRender: false,
        };
    },
    watch: {
        applied_chart_filter: {
            handler(newVal) {
                this.if_chart_filter_applied = newVal?.if_chart_filter_applied && this.id === newVal?.section_chart_id;
                // Sync chart_filter_label from applied_chart_filter when filter is applied to this chart
                if (this.if_chart_filter_applied && newVal?.chart_filters && newVal.chart_filters.length > 0) {
                    this.chart_filter_label = newVal.chart_filters.map(filter => filter.value);
                } else if (!this.if_chart_filter_applied) {
                    this.chart_filter_label = [];
                }
                this.debouncedRender();
            },
            deep: true
        },
        size(newSize, oldSize) {
            if (newSize !== oldSize) {
                this.skipAnimationForNextRender = true;
            }
        },
    },
    methods: {

        /**
         * Calculate how many labels (categories) to show based on chart size
         * Stacked horizontal bar chart truncation logic:
         * - For all chart sizes: if more than 10 data points, show 10 per page
         * - Otherwise, show all data points
         */
        calculateLabelsToShow() {
            const totalLabels = this.chart_selected.labels?.length || 0;
            
            // For all chart sizes: if more than 10 data points, truncate to 10
            if (totalLabels > 10) {
                return 10;
            }
            
            // Otherwise, show all labels
            return totalLabels;
        },
        
        renderChart() {
            if (!this.chart_selected?.data) return;

            // Set pagination and dataset state early for accurate calculations
            this.num_of_datasets = Object.keys(this.chart_selected.data).length;
            this.totalDataLength = this.chart_selected.labels?.length || 0;
            this.displayedDataCount = this.forceShowAllData ? this.totalDataLength : this.calculateLabelsToShow();
            this.isDataTruncated = this.displayedDataCount < this.totalDataLength;

            // Emit state to parent for centralized pagination controls
            this.emitPaginationState();

            // Get full dataset first (will use displayedDataCount for styling)
            const fullDataset = this.getChartData();
            
            // Apply truncation using base mixin method
            const dataset = this.getTruncatedData(fullDataset);
            const options = this.getChartOptions(dataset);

            // First destroy any existing chart
            this.destroyChart();

            // Wait for the next tick to ensure DOM is ready and previous chart is fully destroyed
            this.$nextTick(() => {
                const ctx = document.getElementById('chart-' + this.id);
                if (ctx?.getContext) {
                    try {
                        this.createChartWhenVisible(ctx, options, (chartInstance) => {
                            this.chart = markRaw(chartInstance);
                            this.setupChartMouseLeave(chartInstance);
                            this.skipAnimationForNextRender = false;
                        });
                    } catch (error) {
                        console.error('Error creating chart:', error);
                    }
                }
            });
        },
        debouncedRender() {
            if (this.render_timeout) {
                clearTimeout(this.render_timeout);
            }
            this.render_timeout = setTimeout(() => {
                this.$nextTick(() => {
                    this.format_type = this.chart_selected?.metric_formats?.[0] || 'number';
                    this.color = this.chart_selected?.metrics?.[0]?.color || 'yellow';
                    this.num_of_datasets = Object.keys(this.chart_selected.data).length;
                    this.if_chart_has_data = true;
                    this.renderChart();
                    this.emitPaginationState();
                });
            }, 200); // 200ms debounce
        },
        getBorderRadiusByDataLength(dataLength) {
            if (dataLength < 5) {
                return 10;
            } else if (dataLength < 10) {
                return 7;
            } else if (dataLength < 15) {
                return 5;
            } else {
                return 3;
            }
        },
        /**
         * Calculates a nice rounded-up maximum value for the x-axis
         * Logic: if (top_axis - highest_bar) < 35% of the space between axis ticks, add additional top axis
         */
        getXAxisMax(maxValue) {
            if (maxValue <= 0) {
                return 1;
            }
            
            // Calculate the order of magnitude
            const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
            const normalized = maxValue / magnitude;
            
            // Calculate natural top axis (round up to next nice number)
            let naturalTop;
            if (normalized <= 1) {
                naturalTop = 1 * magnitude;
            } else if (normalized <= 1.5) {
                naturalTop = 1.5 * magnitude;
            } else if (normalized <= 2) {
                naturalTop = 2 * magnitude;
            } else if (normalized <= 2.5) {
                naturalTop = 2.5 * magnitude;
            } else if (normalized <= 4) {
                naturalTop = 4 * magnitude;
            } else if (normalized <= 5) {
                naturalTop = 5 * magnitude;
            } else if (normalized <= 10) {
                naturalTop = 10 * magnitude;
            } else {
                naturalTop = magnitude * 10;
            }
            
            // Calculate gap between natural top and highest bar
            const gap = naturalTop - maxValue;
            
            // Calculate 35% of the space between axis ticks
            // For maxTicksLimit: 5, the interval is approximately naturalTop / 4
            const interval = naturalTop / 4;
            const threshold = interval * 0.35;
            
            // If gap is less than 35% of interval, add additional top axis
            if (gap < threshold) {
                // Get next level up
                const naturalTopNormalized = naturalTop / magnitude;
                if (naturalTopNormalized === 1) {
                    return 1.5 * magnitude;
                } else if (naturalTopNormalized === 1.5) {
                    return 2 * magnitude;
                } else if (naturalTopNormalized === 2) {
                    return 2.5 * magnitude;
                } else if (naturalTopNormalized === 2.5) {
                    return 4 * magnitude;
                } else if (naturalTopNormalized === 4) {
                    return 5 * magnitude;
                } else if (naturalTopNormalized === 5) {
                    return 10 * magnitude;
                } else {
                    return naturalTop * 2;
                }
            } else {
                // Gap is sufficient, use natural top axis
                return naturalTop;
            }
        },
        /**
         * Calculates bar thickness based on chart size and data length
         */
        getBarThickness(dataLength) {
            const chartSize = this.chart_selected?.size || 'small';
            
            // Module: always 15px
            if (chartSize === 'module') {
                return 15;
            }
            
            // other: 30px if datalength < 5, else 15px
            if (dataLength <= 5) {
                return 30;
            } else {
                return 15;
            }
        },
        /**
         * Darkens a color by a percentage (0-1)
         * @param {string} color - RGB color string like 'rgb(255 255 255)'
         * @param {number} percent - Percentage to darken (0.2 = 20%)
         * @returns {string} Darkened RGB color string
         */
        darkenColor(color, percent) {
            const rgbMatch = color.match(/rgb\((\d+)\s+(\d+)\s+(\d+)\)/);
            if (!rgbMatch) return color;
            
            const r = Math.max(0, Math.floor(parseInt(rgbMatch[1]) * (1 - percent)));
            const g = Math.max(0, Math.floor(parseInt(rgbMatch[2]) * (1 - percent)));
            const b = Math.max(0, Math.floor(parseInt(rgbMatch[3]) * (1 - percent)));
            
            return `rgb(${r}, ${g}, ${b})`;
        },
        /**
         * Animates hover opacity from current value to target value
         * @param {number} targetOpacity - Target opacity (0 to 1)
         * @param {Object} chart - Chart.js instance
         */
        animateHoverOpacity(targetOpacity, chart) {
            if (this.hoverAnimationFrame) {
                cancelAnimationFrame(this.hoverAnimationFrame);
            }
            
            const startOpacity = this.hoverOpacity;
            const duration = 200; // 200ms smooth fade-in
            const startTime = performance.now();
            
            const animate = (currentTime) => {
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / duration, 1);
                
                // Ease-out function for smooth animation
                const easeOut = 1 - Math.pow(1 - progress, 2);
                this.hoverOpacity = startOpacity + (targetOpacity - startOpacity) * easeOut;
                
                // Check if chart instance is still valid and has a context before updating
                if (chart && chart.ctx) {
                    chart.update('none'); // Update without animation
                }
                
                if (progress < 1) {
                    this.hoverAnimationFrame = requestAnimationFrame(animate);
                } else {
                    this.hoverOpacity = targetOpacity;
                    this.hoverAnimationFrame = null;
                }
            };
            
            this.hoverAnimationFrame = requestAnimationFrame(animate);
        },
        getChartData() {
            var datasetArray = [];
            var data_length = Object.values(this.chart_selected.data)[0].length;
            const borderRadius = this.getBorderRadiusByDataLength(data_length);
            const barThickness = this.getBarThickness(data_length);
            var max = 0;
            var totals = [];

            // Get color indices based on number of datasets
            const colorIndices = this.getColorIndicesForDatasets(this.num_of_datasets);
            const colorArray = this.getColor(this.color);
            // Map indices to actual color values
            const chartColors = colorIndices.map(idx => colorArray[idx]);

            if (this.num_of_datasets > 1) {
                // multiple dataset
                if (this.chart_filter_label.length > 1) { // multiple dimension, should only highlight the selected column within the group
                    this.chart_filter_label_index[0] = this.chart_selected.labels.findIndex(label => (label || '(not set)') == this.chart_filter_label[0]);
                    this.chart_filter_label_index[1] = Object.keys(this.chart_selected.data).findIndex(data => (data || '(not set)') == this.chart_filter_label[1]);
                } else { //single dimension, should highlight the whole selected column
                    this.chart_filter_label_index[0] = this.chart_selected.labels.findIndex(label => (label || '(not set)') == this.chart_filter_label[0]);
                }

                //Calculate the totals for the different columns
                Object.keys(this.chart_selected.data).forEach((key, index) => {

                   for(var i = 0; i < this.chart_selected.data[key].length; i++){
                       var temp = this.chart_selected.data[key][i];
                       if(totals[i] == undefined)
                           totals.push(temp);
                       else
                           totals[i] += temp;
                   }
                })
                //Find the max value in the totals
                max = Math.max(...totals);

                Object.keys(this.chart_selected.data).forEach((key,index) => {
                    // Get color for this dataset (reverse order: last dataset gets first color)
                    const datasetColorIndex = chartColors.length - 1 - index;
                    const datasetColor = chartColors[datasetColorIndex] || chartColors[0];

                    if (this.if_chart_filter_applied) {
                        if (this.chart_filter_label.length > 1) {
                            var backgroundColorArray = Array(this.chart_selected.data[key].length).fill(this.getColorByLetter('gray', 'D'));
                            if ((key || '(not set)') == this.chart_filter_label[1]) {
                                backgroundColorArray[this.chart_filter_label_index[0]] = chartColors[0]; // Use color A
                            }
                            datasetArray.push({
                                label: key==""? '(not set)':key,
                                data: this.chart_selected.data[key],
                                backgroundColor: backgroundColorArray,
                                borderWidth: 0,
                                borderRadius: borderRadius,
                                maxBarThickness: barThickness,
                            });
                        }else {
                            var backgroundColorArray = Array(this.chart_selected.data[key].length).fill(this.getColorByLetter('gray', 'D'));
                            backgroundColorArray[this.chart_filter_label_index[0]] = chartColors[0]; // Use color A
                            datasetArray.push({
                                label: key==""? '(not set)':key,
                                data: this.chart_selected.data[key],
                                backgroundColor: backgroundColorArray,
                                borderWidth: 0,
                                borderRadius: borderRadius,
                                maxBarThickness: barThickness,
                            });
                        }
                    } else if(this.chart_selected.highlight_last_value){
                        const isLastDataset = index == this.num_of_datasets - 1;
                        let backgroundColor;
                        if (isLastDataset) {
                            backgroundColor = chartColors[0]; // Use color A
                        } else {
                            // Map index to gray color (reverse: index 0 -> G, index 1 -> F, etc.)
                            const grayIndex = Math.max(0, Math.min(6 - index - 1, 6));
                            const grayLetter = ['G', 'F', 'E', 'D', 'C', 'B', 'A'][grayIndex] || 'A';
                            backgroundColor = this.getColorByLetter('gray', grayLetter);
                        }
                        datasetArray.push({
                            label: key==""? '(not set)':key,
                            data: this.chart_selected.data[key],
                            backgroundColor: backgroundColor,
                            borderWidth: 0,
                            borderRadius: borderRadius,
                            maxBarThickness: barThickness,
                        });
                    }else{
                        datasetArray.push({
                            label: key==""? '(not set)':key,
                            data: this.chart_selected.data[key],
                            backgroundColor: datasetColor,
                            borderWidth: 0,
                            borderRadius: borderRadius,
                        });
                    }
                });
            } else {
                // single dataset - use color A
                const chartColor = this.getColorByLetter(this.color, 'A');

                Object.keys(this.chart_selected.data).forEach(key => {
                    var max_temp = Math.max(...this.chart_selected.data[key]);
                    if (max_temp > max){
                        max = max_temp;
                    }
                    var backgroundColorArray = [];
                    if (this.if_chart_filter_applied) {
                        var index = this.chart_selected.labels.findIndex(label => (label || '(not set)') == this.chart_filter_label[0]);
                        backgroundColorArray = Array(this.chart_selected.data[key].length).fill(this.getColorByLetter('gray', 'D'));
                        backgroundColorArray[index] = chartColor;
                    } else if(this.chart_selected.highlight_last_value) {
                        backgroundColorArray = Array(this.chart_selected.data[key].length-1).fill(this.getColorByLetter('gray', 'D'));
                        backgroundColorArray.push(chartColor);
                    }
                    else {
                        backgroundColorArray = Array(this.chart_selected.data[key].length).fill(chartColor);
                    }
                    datasetArray.push({
                        label: key==""? '(not set)':key,
                        data: this.chart_selected.data[key],
                        backgroundColor: backgroundColorArray,
                        borderWidth: 0,
                        borderRadius: borderRadius,
                        barThickness: barThickness,
                    });
                });
            }

            var labels_temp = [];
            this.chart_selected.labels.forEach((label)=>{
                if (label){
                    labels_temp.push(label)
                }else{
                    labels_temp.push('(not set)')
                }
            });
            this.max_value = max;
            this.total_values = totals;

            return {
                labels: labels_temp,
                datasets: datasetArray
            };
        },
        getChartOptions(data) {
            var self = this;
            
            // Custom plugin to draw background color for hovered group (Horizontal)
            const hoverBackgroundPlugin = {
                id: 'hoverBackground',
                beforeDatasetsDraw: (chart) => {
                    if (self.hoveredIndex !== null && chart.scales.y) {
                        const ctx = chart.ctx;
                        const xScale = chart.scales.x;
                        const yScale = chart.scales.y;
                        
                        // Get shade E of the metric color with 30% opacity
                        const colorE = self.getColorByLetter(self.color, 'E');
                        const rgbMatch = colorE.match(/rgb\((\d+)\s+(\d+)\s+(\d+)\)/);
                        
                        if (rgbMatch) {
                            const r = rgbMatch[1];
                            const g = rgbMatch[2];
                            const b = rgbMatch[3];
                            const rgbaColor = `rgba(${r}, ${g}, ${b}, ${0.3 * self.hoverOpacity})`;
                            
                            // Get the pixel position for the hovered category (y-axis for horizontal)
                            const pixelValue = yScale.getPixelForValue(self.hoveredIndex);
                            
                            // Calculate category height based on barPercentage and categoryPercentage
                            const categoryPercentage = 0.8; // From datasets.bar.categoryPercentage
                            const totalHeight = yScale.height;
                            const numCategories = yScale.ticks.length;
                            const categorySpacing = totalHeight / numCategories;
                            const categoryHeight = categorySpacing * categoryPercentage;
                            
                            // Calculate y position (center the rectangle on the category)
                            const y = pixelValue - categoryHeight / 2;
                            // Extend width to include y-axis labels
                            // Start from the left edge of the y-axis scale (includes label area)
                            const x = yScale.left;
                            // Extend to the right edge of the chart area
                            const width = chart.chartArea.right - yScale.left;
                            
                            // Calculate radius - ensure it doesn't exceed half the height or width
                            const maxRadius = Math.min(categoryHeight / 2, width / 2, 30);
                            const radius = Math.max(5, maxRadius); // Minimum 5px radius
                            
                            // Draw rounded rectangle
                            ctx.save();
                            ctx.fillStyle = rgbaColor;
                            ctx.beginPath();
                            // Top-left corner
                            ctx.moveTo(x + radius, y);
                            // Top edge
                            ctx.lineTo(x + width - radius, y);
                            // Top-right corner
                            ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
                            // Right edge
                            ctx.lineTo(x + width, y + categoryHeight - radius);
                            // Bottom-right corner
                            ctx.quadraticCurveTo(x + width, y + categoryHeight, x + width - radius, y + categoryHeight);
                            // Bottom edge
                            ctx.lineTo(x + radius, y + categoryHeight);
                            // Bottom-left corner
                            ctx.quadraticCurveTo(x, y + categoryHeight, x, y + categoryHeight - radius);
                            // Left edge
                            ctx.lineTo(x, y + radius);
                            // Top-left corner
                            ctx.quadraticCurveTo(x, y, x + radius, y);
                            ctx.closePath();
                            ctx.fill();
                            ctx.restore();
                        }
                    }
                }
            };
            
            // Custom plugin to darken hovered bars (Horizontal)
            // Uses afterDatasetsDraw (not afterDraw) so datalabels plugin draws on top when hovering
            const darkenBarsPlugin = {
                id: 'darkenBars',
                afterDatasetsDraw: (chart) => {
                    if (self.hoveredIndex !== null) {
                        const ctx = chart.ctx;
                        
                        chart.data.datasets.forEach((dataset, datasetIndex) => {
                            const meta = chart.getDatasetMeta(datasetIndex);
                            if (!meta || meta.hidden) return;
                            
                            const bar = meta.data[self.hoveredIndex];
                            if (!bar) return;
                            
                            const originalColor = Array.isArray(dataset.backgroundColor)
                                ? dataset.backgroundColor[self.hoveredIndex]
                                : dataset.backgroundColor;
                            
                            const darkerColor = self.darkenColor(originalColor, 0.1);
                            
                            const { x, y, base, width, height } = bar.getProps(['x', 'y', 'base', 'width', 'height']);
                            const borderRadius = dataset.borderRadius || 0;
                            const actualRadius = Math.min(borderRadius, width / 2, Math.abs(height) / 2);
                            
                            ctx.save();
                            ctx.fillStyle = darkerColor;
                            
                            const barX = Math.min(x, base);
                            const barWidth = Math.abs(x - base);
                            const barY = y - height / 2;
                            
                            ctx.beginPath();
                            // Start top-left
                            ctx.moveTo(barX, barY);
                            // Top edge to top-right
                            ctx.lineTo(barX + barWidth - actualRadius, barY);
                            // Top-right corner
                            ctx.quadraticCurveTo(barX + barWidth, barY, barX + barWidth, barY + actualRadius);
                            // Right edge to bottom-right
                            ctx.lineTo(barX + barWidth, barY + height - actualRadius);
                            // Bottom-right corner
                            ctx.quadraticCurveTo(barX + barWidth, barY + height, barX + barWidth - actualRadius, barY + height);
                            // Bottom edge to bottom-left
                            ctx.lineTo(barX, barY + height);
                            // Left edge back to top-left
                            ctx.lineTo(barX, barY);
                            ctx.closePath();
                            ctx.fill();
                            ctx.restore();
                        });
                    }
                }
            };
            
            // Custom plugin to draw side labels for all segments when hovering (Horizontal)
            const sideLabelsPlugin = {
                id: 'sideLabels',
                afterDraw: (chart) => {
                    if (self.hoveredIndex !== null && self.hoverOpacity > 0) {
                        const ctx = chart.ctx;
                        const xScale = chart.scales.x;
                        const yScale = chart.scales.y;
                        
                        // Get the first visible bar to determine bar position
                        let firstBar = null;
                        for (let datasetIndex = 0; datasetIndex < chart.data.datasets.length; datasetIndex++) {
                            const meta = chart.getDatasetMeta(datasetIndex);
                            if (meta && !meta.hidden) {
                                const bar = meta.data[self.hoveredIndex];
                                if (bar) {
                                    firstBar = bar;
                                    break;
                                }
                            }
                        }
                        
                        if (!firstBar) return;
                        
                        // Calculate spacing for labels above the bar (from right edge)
                        const spacing = 15; // Space from the right edge of the bar
                        
                        // Iterate through all datasets to show labels for each segment
                        const datasets = chart.data.datasets;
                        for (let datasetIndex = 0; datasetIndex < datasets.length; datasetIndex++) {
                            const dataset = datasets[datasetIndex];
                            const meta = chart.getDatasetMeta(datasetIndex);
                            
                            if (!meta || meta.hidden) continue;
                            
                            const bar = meta.data[self.hoveredIndex];
                            if (!bar) continue;
                            
                            const { x, base, y, height } = bar.getProps(['x', 'base', 'y', 'height']);
                            
                            // Calculate the right edge of this specific segment (for stacked bars)
                            // For horizontal bars, x is the left edge and base is the right edge (or vice versa)
                            const segmentRightEdge = Math.max(x, base);
                            
                            // Position label at the right edge of this segment (above the bar)
                            const labelX = segmentRightEdge + spacing;
                            
                            // Calculate the middle y position of this segment
                            const segmentMiddle = y;
                            
                            // Get the value for this segment
                            const value = dataset.data[self.hoveredIndex];
                            
                            // Format the label
                            let labelText = '';
                            if(self.chart_selected.show_series_percentage){
                                labelText = self.percentage(value);
                            } else {
                                if(self.format_type == 'money') {
                                    labelText = self.show_compressed_numbers && value >= 1000 
                                        ? self.currency_abbr(value) 
                                        : self.currency_with_zero(value);
                                } else if(self.format_type == 'number') {
                                    if(self.show_compressed_numbers && value >= 1000) {
                                        labelText = self.num_abbr(value);
                                    } else if(self.max_value >= 10) {
                                        labelText = self.number_with_zero(value);
                                    } else {
                                        labelText = self.number_with_decimal(value);
                                    }
                                } else if(self.format_type == 'percent') {
                                    labelText = self.percentage(value);
                                } else {
                                    labelText = value.toString();
                                }
                            }
                            
                            // Draw the label
                            ctx.save();
                            ctx.font = `${self.font_size}px 'Nunito', sans-serif`;
                            ctx.fillStyle = `rgba(17, 25, 33, ${self.hoverOpacity})`;
                            ctx.textAlign = 'left';
                            ctx.textBaseline = 'middle';
                            ctx.fillText(labelText, labelX, segmentMiddle);
                            ctx.restore();
                        }
                    }
                }
            };

            // Custom plugin to draw solid line at x=0 (base y-axis) extending across chart area including y-axis labels
            const solidBaseLinePlugin = {
                id: 'solidBaseLine',
                afterDraw: (chart) => {
                    const ctx = chart.ctx;
                    const yScale = chart.scales.y;
                    const xScale = chart.scales.x;
                    
                    // Get the x position for zero
                    const zeroX = xScale.getPixelForValue(0);
                    
                    // Use chart area boundaries and extend to cover y-axis labels
                    // Start from the top edge of the y-axis scale (includes labels)
                    // End at the bottom edge of the y-axis scale (includes labels)
                    const topEdge = yScale.top;
                    const bottomEdge = yScale.bottom;
                    
                    // Draw solid line extending vertically across the chart area and y-axis labels
                    ctx.save();
                    ctx.strokeStyle = 'rgba(206, 219, 215, 1)';
                    ctx.lineWidth = 1;
                    ctx.setLineDash([]); // Solid line - empty array means no dashes
                    ctx.beginPath();
                    ctx.moveTo(zeroX, topEdge);
                    ctx.lineTo(zeroX, bottomEdge);
                    ctx.stroke();
                    ctx.restore();
                }
            };
            
            var config = {
                plugins: this.if_hundred_percent 
                    ? [ChartjsPluginStacked100, solidBaseLinePlugin, hoverBackgroundPlugin, darkenBarsPlugin, ChartDataLabels, sideLabelsPlugin]
                    : [solidBaseLinePlugin, hoverBackgroundPlugin, darkenBarsPlugin, ChartDataLabels, sideLabelsPlugin],
                type: 'bar',
                data: data,

                //All of the special aspects that make these charts unique
                options: {
                    maintainAspectRatio: false,
                    interaction: {
                        intersect: false,
                        mode: 'index', 
                        axis: 'y'
                    },
                    onHover: (event, activeElements, chart) => {
                        if (activeElements && activeElements.length > 0) {
                            // Clear any pending hide timeout when hovering over an element
                            if (this.hideTimeout) {
                                clearTimeout(this.hideTimeout);
                                this.hideTimeout = null;
                            }
                            
                            const activeElement = activeElements[0];
                            const dataIndex = activeElement.index;
                            
                            // If hovering over a different data point OR returning to a point that's not currently hovered,
                            // (re)start the debounce timer
                            if (dataIndex !== this.pendingHoverIndex || this.hoveredIndex !== dataIndex) {
                                // Cancel any pending show timeout
                                if (this.hoverTimeout) {
                                    clearTimeout(this.hoverTimeout);
                                }
                                
                                // Reset current state immediately when moving to a new point
                                // to keep highlight and tooltip in sync (both hidden during the debounce)
                                if (this.hoveredIndex !== null && this.hoveredIndex !== dataIndex) {
                                    this.hoveredIndex = null;
                                    this.hoverOpacity = 0;
                                    chart.update('none');
                                }
                                
                                // Reset flags for new data point
                                this.pendingHoverIndex = dataIndex;
                                
                                // Start new debounce timer - 200ms delay to show hover
                                this.hoverTimeout = setTimeout(() => {
                                    this.hoverTimeout = null;
                                    // Apply hover effect after delay
                                    if (this.hoveredIndex !== dataIndex) {
                                        this.hoveredIndex = dataIndex;
                                        chart.update('none'); // Immediate update for bar colors and tooltip
                                        this.animateHoverOpacity(1, chart);
                                    }
                                }, 200);
                            }
                        } else {
                            // Clear any pending show timeout when leaving
                            if (this.hoverTimeout) {
                                clearTimeout(this.hoverTimeout);
                                this.hoverTimeout = null;
                                this.pendingHoverIndex = null;
                            }
                            
                            // Debounce hiding the hover state (200ms delay to match tooltip)
                            if (this.hoveredIndex !== null && !this.hideTimeout) {
                                this.hideTimeout = setTimeout(() => {
                                    // Only hide if still not hovering and tooltip is also not being hovered
                                    if (this.hoveredIndex !== null && !this.isTooltipHovered) {
                                        this.hoveredIndex = null;
                                        chart.update('none'); // Immediate update for bar colors and tooltip
                                        this.animateHoverOpacity(0, chart);

                                        // Explicitly hide tooltip element to stay in sync
                                        let tooltipEl = document.getElementById('chart-' + this.id + '-' + this.effectiveSectionId + '-tooltip');
                                        if (tooltipEl) {
                                            tooltipEl.style.opacity = 0;
                                            tooltipEl.style.pointerEvents = 'none';
                                        }
                                    }
                                    this.hideTimeout = null;
                                }, 200);
                            }
                        }
                    },
                    animation: {
                        duration: this.skipAnimationForNextRender ? 0 : 1000,
                    },
                    indexAxis: 'y',
                    responsive: true,
                    animations: {
                        y: {
                            duration: 1000,
                        },
                    },
                    datasets: {
                        bar: {
                            barPercentage: 0.6,
                            categoryPercentage: 0.8,
                        }
                    },
                    layout: {
                        padding: {
                            right: 50,
                            bottom: 10
                        }
                    },
                    scales: {
                        y: {
                            stacked: true,
                            grid: {
                                drawBorder: false,
                                display : false
                            },
                            ticks: {
                                font: {
                                    size: this.font_size,
                                    weight: 400,
                                    family: "'Nunito', sans-serif",
                                },
                                maxRotation:0,
                                autoSkip:false,
                                callback: function(value, index, ticks, context) {
                                    // Wrap around long labels - use truncated data labels
                                    var label_substrings = '';
                                    if(data.labels && data.labels[value]){
                                        label_substrings = data.labels[value];
                                    } else {
                                        label_substrings = '(not set)';
                                    }
                                    return label_substrings;
                                }
                            }
                        },
                        x: {
                            beginAtZero: true,
                            stacked: true,
                            // For 100% charts, let the stacked100 plugin control the 0-100 scale
                            ...(this.if_hundred_percent ? {} : { max: this.getXAxisMax(this.max_value) }),
                            grid: {
                                drawOnChartArea: true,
                                drawBorder: false,
                                color: 'rgba(206, 219, 215, 1)',
                                lineWidth: 1,
                                borderDash: [2,2],
                                drawTicks: true,
                                tickColor: 'rgba(206, 219, 215, 1)',
                                tickLength: 20,
                                tickBorderDash: [2,2],
                                tickBorderDashOffset: 0,
                                z: -2,
                            },
                            ticks: {
                                font: {
                                    size: this.font_size,
                                    weight: 400,
                                    family: "'Nunito', sans-serif",
                                },
                                autoSkip: true,
                                maxTicksLimit: 5,
                                callback: function(value, index, ticks, context) {
                                    if(self.if_hundred_percent)
                                        return self.number_with_decimal(value) + '%';
                                    if(self.format_type == 'money') {
                                        if(self.show_compressed_numbers && value >= 1000) {
                                            return self.currency_abbr(value);
                                        }
                                        return self.currency_with_zero(value);
                                    }
                                    if(self.format_type == 'number') {
                                        if(self.show_compressed_numbers && value >= 1000) {
                                            return self.num_abbr(value);
                                        }
                                        if(self.max_value >= 10) {
                                            return self.number_with_zero(value);
                                        }
                                        return self.number_with_decimal(value);
                                    }
                                    if(self.format_type == 'percent')
                                        return self.percentage(value);
                                }
                            }
                        },
                    },
                    plugins: {
                        legend: {
                            position: (data.datasets.length > 1) ? 'bottom' : 'none',
                            align: 'center',
                            fullSize: true,
                            onClick: (e, legendItem, legend) => {
                                const chart = legend.chart;
                                const datasetIndex = legendItem.datasetIndex;

                                // toggle dataset visibility
                                chart.setDatasetVisibility(datasetIndex, !chart.isDatasetVisible(datasetIndex));
                                chart.update();
                            },
                            onHover: function(e, chart) {
                                e.native.target.style.cursor = 'pointer';
                            },
                            onLeave: function(e, chart) {
                                e.native.target.style.cursor = 'default';
                            },
                            labels: {
                                usePointStyle: true,
                                pointStyle: 'circle',
                                padding: 20,
                                generateLabels: (chart) => {
                                    return chart.data.datasets.map((dataset, index) => {
                                        const fill = Object.keys(this.chart_selected.data).length > 1
                                        ? dataset.backgroundColor
                                        : dataset.backgroundColor.slice(-1);

                                        return {
                                            text: dataset.label,
                                            datasetIndex: index,
                                            hidden: !chart.isDatasetVisible(index),
                                            pointStyle: 'circle',
                                            fillStyle: fill,
                                            lineWidth: 0,
                                        };
                                    }); 
                                },
                                font: {
                                    size: this.font_size,
                                    weight: 400,
                                    family: "'Nunito', sans-serif",
                                },
                            }
                        },
                        tooltip: {
                            enabled: false,
                            external: function(context) {
                                const chartCanvasId = context.chart?.canvas?.id;
                                if (!chartCanvasId || !chartCanvasId.startsWith('chart-')) return;

                                const chartIdFromCanvas = chartCanvasId.replace(/^chart-/, '');
                                const expectedTooltipId = self.getTooltipId();
                                if (chartIdFromCanvas !== String(self.id)) {
                                    let tooltipEl = document.getElementById(expectedTooltipId);
                                    if (tooltipEl) {
                                        tooltipEl.style.opacity = 0;
                                        tooltipEl.style.pointerEvents = 'none';
                                    }
                                    return;
                                }

                                let tooltipEl = document.getElementById('chart-'+self.id+'-'+self.effectiveSectionId+'-tooltip');

                                if (!tooltipEl) {
                                    tooltipEl = document.createElement('div');
                                    tooltipEl.id = 'chart-'+self.id+'-'+self.effectiveSectionId+'-tooltip';
                                    tooltipEl.classList.add("chart-template-tooltip");
                                    tooltipEl.innerHTML = '<table></table>';
                                    document.body.appendChild(tooltipEl);

                                    tooltipEl.addEventListener('wheel', (e) => {
                                        e.preventDefault();
                                        const deltaY = e.deltaY;
                                        if (deltaY) {
                                            window.scrollBy({ top: deltaY, behavior: 'auto' });
                                        }
                                    }, { passive: false });

                                    tooltipEl.addEventListener('mouseenter', () => {
                                        if (self.hideTimeout) {
                                            clearTimeout(self.hideTimeout);
                                            self.hideTimeout = null;
                                        }
                                        self.isTooltipHovered = true;
                                        tooltipEl.style.opacity = 1;
                                        tooltipEl.style.pointerEvents = 'auto';
                                    });
                                    tooltipEl.addEventListener('mouseleave', () => {
                                        self.isTooltipHovered = false;
                                        
                                        if (self.hideTimeout) {
                                            clearTimeout(self.hideTimeout);
                                        }
                                        self.hideTimeout = setTimeout(() => {
                                            if (!self.isTooltipHovered && tooltipEl) {
                                                tooltipEl.style.opacity = 0;
                                                tooltipEl.style.pointerEvents = 'none';
                                            }
                                            self.hideTimeout = null;
                                        }, 200);
                                    });
                                }

                                const tooltipIdPrefix = 'chart-' + chartIdFromCanvas + '-';
                                if (!tooltipEl.id.startsWith(tooltipIdPrefix) || !tooltipEl.id.endsWith('-tooltip') || tooltipEl.id !== expectedTooltipId) {
                                    tooltipEl.style.opacity = 0;
                                    tooltipEl.style.pointerEvents = 'none';
                                    return;
                                }

                                const position = context.chart.canvas.getBoundingClientRect();
                                const tooltipModel = context.tooltip;

                                if (!tooltipModel || tooltipModel.opacity === 0) {
                                    if (!self.isTooltipHovered && self.hoveredIndex === null && tooltipEl) {
                                        tooltipEl.style.opacity = 0;
                                        tooltipEl.style.pointerEvents = 'none';
                                        self.hideAllChartTooltips();
                                    }
                                    return;
                                }
                                
                                const currentDataIndex = tooltipModel.dataPoints && tooltipModel.dataPoints.length > 0 
                                    ? tooltipModel.dataPoints[0].dataIndex 
                                    : null;
                                
                                if (currentDataIndex === null || currentDataIndex !== self.hoveredIndex) {
                                    tooltipEl.style.opacity = 0;
                                    tooltipEl.style.pointerEvents = 'none';
                                    self.hideAllChartTooltips();
                                    return;
                                }

                                // Determine which group is hovered for smart positioning
                                const dataIndex = tooltipModel.dataPoints && tooltipModel.dataPoints.length > 0 
                                    ? tooltipModel.dataPoints[0].dataIndex 
                                    : null;
                                const totalGroups = context.chart.data.labels.length;
                                const isFirstHalf = dataIndex !== null && dataIndex < totalGroups / 2;

                                // Hide tooltips from other sections
                                const allTooltips = document.querySelectorAll('[id^="chart-"][id$="-tooltip"]');
                                allTooltips.forEach(tt => {
                                    if (tt.id !== tooltipEl.id) {
                                        tt.style.opacity = 0;
                                        tt.style.pointerEvents = 'none';
                                    }
                                });

                                // Display, position, and set styles for font with glassmorphism effect
                                tooltipEl.style.opacity = 1;
                                tooltipEl.style.pointerEvents = 'auto';
                                tooltipEl.style.position = 'absolute';
                                tooltipEl.style.backgroundColor = 'rgba(255, 255, 255, 0.7)';
                                tooltipEl.style.webkitBackdropFilter = 'blur(30px) saturate(180%)';
                                tooltipEl.style.backdropFilter = 'blur(30px) saturate(180%)';
                                tooltipEl.style.padding = '10px 20px';
                                tooltipEl.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                                tooltipEl.style.border = '1px solid rgba(255, 255, 255, 0.3)';
                                tooltipEl.style.fontFamily = "'Nunito', sans-serif";
                                tooltipEl.style.transition = 'opacity 300ms ease-out';
                                tooltipEl.style.zIndex = '1000';

                                if (tooltipModel.body) {
                                    const titleLines = tooltipModel.title || [];
                                    const bodyLines = tooltipModel.body.map(b => b.lines);

                                    let innerHtml = '<thead>';
                                    innerHtml += '<tr><th style="font-weight: 500; font-size: 18px; padding-bottom: 4px; color: #111921 text-align: left;">' + titleLines[0] + '</th></tr>';
                                    innerHtml += '</thead><tbody>';

                                    let tooltipBody = '';

                                    bodyLines.forEach((body, i) => {
                                        if (!body || !body[0]) return;

                                        const body_label_temp = body[0].split(':');
                                        const body_label = body_label_temp.slice(0, body_label_temp.length - 1).join(':');
                                        const value = tooltipModel.dataPoints[i]?.raw;

                                        if (value === undefined) return;
                                        
                                        let label;
                                        if(self.if_hundred_percent){
                                            if(self.format_type == 'money' && self.max_value >= 10)
                                                label = body_label + ': ' + self.currency_with_zero(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]) + ' (<em>'+ self.percentage(value / self.total_values[context.tooltip.dataPoints[i].dataIndex])  +'</em>)';
                                            if(self.format_type == 'money' && self.max_value < 10)
                                                label = body_label + ': ' + self.currency_with_decimal(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]) + ' (<em>'+ self.percentage(value / self.total_values[context.tooltip.dataPoints[i].dataIndex])  +'</em>)';
                                            if(self.format_type == 'number' && self.max_value >= 10)
                                                label = body_label + ': ' + self.number_with_zero(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]) + ' (<em>'+ self.percentage(value / self.total_values[context.tooltip.dataPoints[i].dataIndex]) +'</em>)';
                                            if(self.format_type == 'number' && self.max_value < 10)
                                                label = body_label + ': ' + self.number_with_decimal(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]) + ' (<em>'+ self.percentage(value / self.total_values[context.tooltip.dataPoints[i].dataIndex]) +'</em>)';
                                            if(self.format_type == 'percent')
                                                label = body_label + ': ' + self.percentage(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]) + ' (<em>'+ self.percentage(value / self.total_values[context.tooltip.dataPoints[i].dataIndex])+'</em>)';
                                        } else{
                                            if(self.format_type == 'money' && self.max_value >= 10)
                                                label = body_label + ': ' + self.currency_with_zero(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]) + ' (<em>'+ self.percentage(value / self.total_values[context.tooltip.dataPoints[i].dataIndex])  +'</em>)';
                                            if(self.format_type == 'money' && self.max_value < 10)
                                                label = body_label + ': ' + self.currency_with_decimal(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]) + ' (<em>'+ self.percentage(value / self.total_values[context.tooltip.dataPoints[i].dataIndex])  +'</em>)';
                                            if(self.format_type == 'number' && self.max_value >= 10)
                                                label = body_label + ': ' + self.number_with_zero(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]) + ' (<em>'+ self.percentage(value / self.total_values[context.tooltip.dataPoints[i].dataIndex]) +'</em>)';
                                            if(self.format_type == 'number' && self.max_value < 10)
                                                label = body_label + ': ' + self.number_with_decimal(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]) + ' (<em>'+ self.percentage(value / self.total_values[context.tooltip.dataPoints[i].dataIndex]) +'</em>)';
                                            if(self.format_type == 'percent')
                                                label = body_label + ': ' + self.percentage(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]) + ' (<em>'+ self.percentage(value / self.total_values[context.tooltip.dataPoints[i].dataIndex])+'</em>)';
                                        }

                                        const colors = tooltipModel.labelColors[i];
                                        const textColor = colors.backgroundColor;

                                        let formattedValue = '';
                                        let percentageText = '';

                                        if (self.num_of_datasets > 1) {
                                            if (self.format_type === 'money') {
                                                formattedValue = self.currency_with_zero(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]);
                                            } else if (self.format_type === 'number' && self.max_value >= 10) {
                                                formattedValue = self.number_with_zero(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]);
                                            } else if (self.format_type === 'number' && self.max_value < 10) {
                                                formattedValue = self.number_with_decimal(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]);
                                            } else if (self.format_type === 'percent') {
                                                formattedValue = self.percentage(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]);
                                            }
                                            
                                            const total = self.total_values[context.tooltip.dataPoints[i].dataIndex];
                                            percentageText = ' (<em>'+ self.percentage(value / total) +'</em>)';

                                            if(self.if_benchmark_report){
                                                tooltipBody = '<tr><td class="d-flex align-items-center"><span style="color: ' + textColor + '; font-weight: 400;">' + body_label + '</span>: <span style="color: #111921; font-weight: 400; margin-left: 4px;">' + formattedValue + percentageText + '</span></td></tr>' + tooltipBody;
                                            } else {
                                                tooltipBody = '<tr><td class="d-flex align-items-center py-1"><span class="flex-grow-1"><span style="color: ' + textColor + '; font-weight: 400;">' + body_label + '</span>: <span style="color: #111921; font-weight: 400; margin-left: 4px;">' + formattedValue + percentageText + '</span></span><button id="'+ i +'" class="btn btn-sm btn-white tooltip-btn-white btn-explore-individual p-0 ms-2" style="width:24px !important; height: 24px !important; border-radius: 50% !important; display: flex !important; align-items: center !important; justify-content: center !important;"><img class="icon" src="/img/icons/avid-icons/filter-funnel-01.svg" style="width: 14px; height: 14px; margin: 0 !important;" alt=""></button></td></tr>' + tooltipBody;
                                            }
                                        } else {
                                            if (self.format_type === 'money') {
                                                formattedValue = self.currency_with_zero(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]);
                                            } else if (self.format_type === 'number' && self.max_value >= 10) {
                                                formattedValue = self.number_with_zero(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]);
                                            } else if (self.format_type === 'number' && self.max_value < 10) {
                                                formattedValue = self.number_with_decimal(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]);
                                            } else if (self.format_type === 'percent') {
                                                formattedValue = self.percentage(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]);
                                            }
                                            tooltipBody += '<tr><td style="padding-top: 1px; padding-bottom: 1px;"><span class="tooltip-single-dataset-value" style="color: #111921; font-weight: 400; font-size: 32px;">' + formattedValue + '</span></td></tr>';
                                        }
                                    });
                                    innerHtml += tooltipBody;

                                    if(self.if_on_report_page && !self.if_benchmark_report){
                                        const isFilterApplied = self.if_chart_filter_applied && self.chart_filter_label[0] == titleLines[0];

                                        innerHtml += '<div class="d-flex justify-content-end my-2">' +
                                            '<button class="btn btn-sm btn-primary d-flex align-items-center px-4" style="font-weight: 500;">' +
                                            '<img class="icon me-1" src="/img/icons/avid-icons/filter-funnel-01.svg" alt="">' + (isFilterApplied ? 'Remove as Filter' : 'Filter') +
                                            '</button>' +
                                            '</div>';

                                        innerHtml += '</tbody>';

                                        let tableRoot = tooltipEl.querySelector('table');
                                        tableRoot.innerHTML = innerHtml;
                                        tableRoot.style.width = '100%';

                                        let btnExploreIndividuals = tooltipEl.querySelectorAll('.btn-explore-individual');
                                        if (self.if_chart_filter_applied){
                                            btnExploreIndividuals.forEach((btn)=>{
                                                btn.hidden = true;
                                            })
                                        } else {
                                            btnExploreIndividuals.forEach((btn)=>{
                                                btn.hidden = false;
                                                btn.addEventListener('click', (event) => {
                                                    self.if_chart_filter_applied = true;
                                                    self.chart_filter_label = [];

                                                    var filter_array = [];
                                                    self.chart_selected.group_by.forEach((dimension, index) => {
                                                        let value_temp = null;
                                                        if (index === 0) {
                                                            value_temp = titleLines[0];
                                                        } else if (index === 1) {
                                                            const datasetIndex = parseInt(event.currentTarget.id);
                                                            const bodyLine = bodyLines[datasetIndex]?.[0];
                                                            if (bodyLine) {
                                                                const body_label_temp = bodyLine.split(':');
                                                                value_temp = body_label_temp.slice(0, body_label_temp.length - 1).join(':').trim();
                                                            }
                                                        }
                                                        
                                                        if (value_temp) {
                                                            self.chart_filter_label.push(value_temp);
                                                            filter_array.push({
                                                                name: dimension.text,
                                                                column: dimension.value,
                                                                value: value_temp
                                                            })
                                                        }
                                                    })
                                                    self.$emit('exploreChart', {
                                                        chart_selected: self.chart_selected,
                                                        chart_filters: filter_array
                                                    });
                                                });
                                            });
                                        }

                                        let btnExploreAll = tooltipEl.querySelector('.btn-primary');

                                        btnExploreAll.disabled = true;
                                        if (!self.applied_chart_filter.if_chart_filter_applied){
                                            btnExploreAll.disabled = false;
                                        } else {
                                            if(self.if_chart_filter_applied && self.chart_filter_label[0] == titleLines[0]){
                                                btnExploreAll.disabled = false;
                                            }
                                        }

                                        if (self.if_chart_filter_applied){
                                            btnExploreAll.addEventListener('click', () => {
                                                self.if_chart_filter_applied = false;
                                                self.chart_filter_label = [];
                                                self.$emit('exploreChart', {
                                                    chart_selected: null,
                                                    chart_filters: []
                                                });
                                            });
                                        } else {
                                            btnExploreAll.addEventListener('click', () => {
                                                self.if_chart_filter_applied = true;
                                                self.chart_filter_label = [titleLines[0]];

                                                self.$emit('exploreChart', {
                                                    chart_selected: self.chart_selected,
                                                    chart_filters: [{
                                                        name: self.chart_selected.group_by[0].text,
                                                        column: self.chart_selected.group_by[0].value,
                                                        value: titleLines[0]
                                                    }]
                                                });

                                                btnExploreAll.innerHTML = '<img class="icon" src="/img/icons/avid-icons/filter-funnel-01.svg" alt="">' + "Remove as Filter";
                                            });
                                        }
                                    } else {
                                        innerHtml += '</tbody>';
                                        let tableRoot = tooltipEl.querySelector('table');
                                        tableRoot.innerHTML = innerHtml;
                                    }
                                }

                                // Position tooltip smart
                                const tooltipWidth = tooltipEl.offsetWidth || 200;
                                const tooltipHeight = tooltipEl.offsetHeight || 100;
                                
                                if (isFirstHalf) {
                                    // Top half of chart: show tooltip below the bar
                                    tooltipEl.style.left = position.left + window.scrollX + tooltipModel.caretX - tooltipWidth/2 + 'px';
                                    tooltipEl.style.top = position.top + window.scrollY + tooltipModel.caretY + 20 + 'px';
                                    tooltipEl.classList.remove('above');
                                    tooltipEl.classList.add('below');
                                } else {
                                    // Bottom half: show tooltip above the bar
                                    tooltipEl.style.left = position.left + window.scrollX + tooltipModel.caretX - tooltipWidth/2 + 'px';
                                    tooltipEl.style.top = position.top + window.scrollY + tooltipModel.caretY - tooltipHeight - 20 + 'px';
                                    tooltipEl.classList.remove('below');
                                    tooltipEl.classList.add('above');
                                }
                            }
                        },
                        datalabels: {
                            anchor: function(context) {
                                const chart = context.chart;
                                const dataIndex = context.dataIndex;
                                const total = chart.data.datasets.reduce((sum, ds) => {
                                    const v = ds.data[dataIndex];
                                    return sum + (typeof v === 'number' ? v : 0);
                                }, 0);
                                return total >= 0 ? 'end' : 'start';
                            },
                            align: function(context) {
                                const chart = context.chart;
                                const dataIndex = context.dataIndex;
                                const total = chart.data.datasets.reduce((sum, ds) => {
                                    const v = ds.data[dataIndex];
                                    return sum + (typeof v === 'number' ? v : 0);
                                }, 0);
                                return total >= 0 ? 'end' : 'start';
                            },
                            offset: 4,
                            color: function(context) {
                                const baseColor = '#5f687c';
                                // If labels are always shown, return full opacity
                                if (self.if_show_label) {
                                    return baseColor;
                                }
                                // For hover labels, apply animated opacity
                                if (self.hoveredIndex !== null && context.dataIndex === self.hoveredIndex) {
                                    return `rgba(17, 25, 33, ${self.hoverOpacity})`;
                                }
                                return baseColor;
                            },
                            display: function(context) {
                                // Only show labels on top dataset (last dataset index) for stacked charts
                                const isTopDataset = context.datasetIndex == Object.keys(self.chart_selected.data).length - 1;
                                if (!isTopDataset) {
                                    return false;
                                }
                                
                                // If toggle is explicitly ON, always show labels on top dataset
                                if (self.if_show_label === true) {
                                    const meta = context.chart.getDatasetMeta(context.datasetIndex);
                                    return !meta || !meta.hidden;
                                }
                                
                                // If toggle is explicitly OFF, never show labels (user has toggled off)
                                if (self.if_show_label === false) {
                                    return false;
                                }
                                
                                // When toggle is not explicitly set, apply size-based rules
                                const chartSize = self.size || self.chart_selected?.size || 'small';
                                const totalDataPoints = self.chart_selected.labels?.length || 0;
                                
                                if (chartSize === 'small') {
                                    // Small: always hide data labels when toggle is not set
                                    return false;
                                } else if (chartSize === 'medium') {
                                    // Medium: if more than 5 datapoints, hide all data labels
                                    // if datapoints length <= 5, show all data labels
                                    return totalDataPoints <= 5;
                                } else if (chartSize === 'large' || chartSize === 'x-large') {
                                    // Large and x-large: show data labels by default
                                    return true;
                                }
                                
                                return false;
                            },
                            font: {
                                weight: 'regular',
                                size: this.font_size,
                            },
                            formatter: function(value, context) {
                                // Only format labels when if_show_label is true or show_all_data_labels is true
                                // Hover labels are handled by sideLabelsPlugin
                                if (!self.chart_selected.show_all_data_labels && !self.if_show_label){
                                    return null;
                                }
                                if (context.datasetIndex == Object.keys(self.chart_selected.data).length - 1){
                                    var label;
                                    var value_temp = self.total_values[context.dataIndex];
                                    if(self.format_type == 'money') {
                                        if(self.show_compressed_numbers && value_temp >= 1000) {
                                            label = self.currency_abbr(value_temp);
                                        } else if(self.max_value >= 10) {
                                            label = self.currency_with_zero(value_temp);
                                        } else {
                                            label = self.currency_with_decimal(value_temp);
                                        }
                                    } else if(self.format_type == 'number') {
                                        if(self.show_compressed_numbers && value_temp >= 1000) {
                                            label = self.num_abbr(value_temp);
                                        } else if(self.max_value >= 10) {
                                            label = self.number_with_zero(value_temp);
                                        } else {
                                            label = self.number_with_decimal(value_temp);
                                        }
                                    } else if(self.format_type == 'percent') {
                                        label = self.percentage(value_temp);
                                    }

                                    return label;
                                } else {
                                    return null;
                                }
                            },
                        },
                        stacked100: { enable: this.if_hundred_percent, replaceTooltipLabel: false }
                    },
                    title: {
                        display:false,
                    },
                }
           };
           return config;
       }
   }
});
</script>
